import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from './cn';
import { IconButton } from './Button';

type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<AlertTone, { box: string; icon: ReactNode }> = {
  info: { box: 'border-info-line bg-info-soft [&>svg]:text-info-ink', icon: <Info aria-hidden /> },
  success: { box: 'border-success-line bg-success-soft [&>svg]:text-success-ink', icon: <CheckCircle2 aria-hidden /> },
  warning: { box: 'border-warning-line bg-warning-soft [&>svg]:text-warning-ink', icon: <AlertTriangle aria-hidden /> },
  danger: { box: 'border-danger-line bg-danger-soft [&>svg]:text-danger-ink', icon: <XCircle aria-hidden /> },
};

export interface AlertProps {
  tone?: AlertTone;
  title: ReactNode;
  children?: ReactNode;
  /** A link or small button at the right (e.g. "Change password"). */
  action?: ReactNode;
  /** Shows a close button. */
  onDismiss?(): void;
  className?: string;
}

/** An inline message about the page or a section: errors, warnings, notices. */
export function Alert({ tone = 'info', title, children, action, onDismiss, className }: AlertProps) {
  const { t } = useTranslation();
  const look = TONES[tone];
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm text-ink [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0', look.box, className)}
    >
      {look.icon}
      <div className="min-w-0 flex-1">
        <div className="font-bold">{title}</div>
        {children && <div className="mt-0.5 text-ink-muted">{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
      {onDismiss && <IconButton label={t('actions.dismiss')} icon={<X />} size="sm" onClick={onDismiss} className="-my-1 -mr-1.5" />}
    </div>
  );
}
